// Floating "peek" popover that shows a card's FULL content. Two modes:
//   - view: read-only render with markdown body, edges, broken refs
//   - edit: in-place form (kind / title / body / pack) with save/delete
//
// Triggered by double-clicking a CardRow (view) or pressing ⌘/Ctrl + E on a
// selected card (edit). Anchored to the left of the originating card so the
// user can see the discussion underneath.
//
// Closes on: × button, Esc, click outside. In edit mode with unsaved changes,
// closing routes through the dirty-guard ConfirmDialog (handled by App).
//
// Why edit lives in the peek popover instead of a dedicated right column:
// editing is high-frequency and low-stakes; keeping it inline means no visual
// jump from "looking at this card" to "editing this card", and frees the
// fourth column for the gap ledger (its designed purpose per uc-02 prototype).
//
// Why a peek and not just expanding the card row in place? Bodies can be long
// (15+ lines of markdown with headings, lists, tables) and the cards panel is
// already a dense vertical column. Expanding inline pushes other cards far
// down. A floating popover keeps the spatial reading position stable.

import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CardBodyMarkdown } from './CardBodyMarkdown';
import { HistoryDrawer } from './HistoryDrawer';
import type { Card, ImplementationStatus } from '../../api';
import { KIND_SPECS, specForKind } from '../../kinds';
import { parseEdges, parseAllRefs, edgeArrow, edgeColor } from '../../edges';
import { ImplementationStatusPill } from './CardRow';
import { ComputedBadge, ComputedDetail, getCardComputed } from './ComputedProvenance';

export interface CardPeekProps {
  card: Card;
  /** Bounding rect of the originating CardRow at the time of double-click. */
  anchor: DOMRect | null;
  /** Card ids that exist in the project. Used to flag broken `[[…]]` refs. */
  knownIds: ReadonlySet<string>;
  /** Initial mode — 'view' on double-click, 'edit' when entered via `e`. */
  initialMode?: 'view' | 'edit';
  onClose: () => void;
  /** Save edits. Resolves once the patch lands; throws to keep the form open. */
  onSave: (patch: { kind: string; title: string; body: string; pack: string }) => Promise<void>;
  /** Delete the card. `reason` (when provided) is persisted on the
   *  card_history row so the Deleted view can show why it was removed. */
  onDelete: (reason?: string) => Promise<void>;
  /** Bubbles dirty-state up so App's confirmIfDirty can guard cross-card switches. */
  onDirtyChange?: (dirty: boolean) => void;
  /** Atomic edit-and-promote for `gap_kind=interpretation_needed` cards. When
   *  provided AND the card is a proposal, EditBody shows a "Save & Approve"
   *  button alongside Save — one round-trip applies title/body edits and
   *  flips kind to `proposed_kind` on the server. Resolves on success. */
  onSaveAndApprove?: (patch: { title: string; body: string }) => Promise<void>;
  /** Click on a `[[c-X]]` mention or edge chip inside the peek. Closes the
   *  peek (after the dirty-guard) and selects the target card so the panel
   *  scrolls it into view. */
  onJumpToCard?: (id: string) => void;
  /** Project the card belongs to. Required for the History drawer (which
   *  fetches `/api/projects/:pid/cards/:cid/history`) and for restore. */
  projectId?: string;
  /** Called after a successful restore so the parent re-fetches the card.
   *  Optional — when omitted, the History drawer hides its Restore button. */
  onRestored?: () => void;
  /** Open the history modal for an arbitrary card id (typically a deleted
   *  one referenced by broken `[[…]]`). Distinct from the peek's own
   *  internal history toggle, which always targets `card`. */
  onShowHistory?: (cardId: string) => void;
  /** PR-41: flip the card's `implementation_status`. The footer renders
   *  a small "Mark status" dropdown when this is provided AND the card
   *  already has a non-null status (i.e. the user has opted into
   *  tracking via CLI/MCP). For untracked cards the dropdown stays
   *  hidden — flipping NULL → "proposed" from the UI is a Phase B
   *  concern. Pass `null` to clear, or any allowlist value to set. */
  onSetImplementationStatus?: (
    status: ImplementationStatus | null,
  ) => Promise<void>;
  /** Lens MVP: toggle the card's `archived` flag. When provided,
   *  ViewBody renders an "archive" / "unarchive" button next to
   *  history. Server broadcasts CardUpdated so the lens auto-recovers
   *  in this and other tabs. */
  onArchive?: (archived: boolean) => Promise<void>;
}

const POPOVER_WIDTH = 520;
const VIEWPORT_PAD = 16;

export function CardPeek({
  card,
  anchor,
  knownIds,
  initialMode = 'view',
  onClose,
  onSave,
  onDelete,
  onDirtyChange,
  onSaveAndApprove,
  onJumpToCard,
  projectId,
  onRestored,
  onShowHistory,
  onSetImplementationStatus,
  onArchive,
}: CardPeekProps) {
  const ref = useRef<HTMLDivElement>(null);
  const [mode, setMode] = useState<'view' | 'edit'>(initialMode);
  const [showHistory, setShowHistory] = useState(false);
  const spec = specForKind(card.kind);

  // Reset mode when card identity changes — switching cards in view mode
  // shouldn't carry an "I was editing" flag.
  useEffect(() => {
    setMode(initialMode);
    setShowHistory(false);
  }, [card.card_id, initialMode]);

  // Edit-mode draft state. Lives at parent so we can compute dirty + reset
  // on card change. EditView reads/writes via callbacks.
  const [draftKind, setDraftKind] = useState(card.kind);
  const [draftTitle, setDraftTitle] = useState(card.title ?? '');
  const [draftBody, setDraftBody] = useState(card.body);
  const [draftPack, setDraftPack] = useState(card.pack ?? '');
  const [otherKind, setOtherKind] = useState('');

  useEffect(() => {
    setDraftKind(card.kind);
    setDraftTitle(card.title ?? '');
    setDraftBody(card.body);
    setDraftPack(card.pack ?? '');
    setOtherKind('');
  }, [card.card_id, card.kind, card.title, card.body, card.pack]);

  const dirty = useMemo(
    () =>
      mode === 'edit' &&
      (draftKind !== card.kind ||
        draftTitle !== (card.title ?? '') ||
        draftBody !== card.body ||
        draftPack !== (card.pack ?? '')),
    [mode, draftKind, draftTitle, draftBody, draftPack, card],
  );

  const dirtyRef = useRef(false);
  useEffect(() => {
    if (dirtyRef.current !== dirty) {
      dirtyRef.current = dirty;
      onDirtyChange?.(dirty);
    }
  }, [dirty, onDirtyChange]);

  const [position, setPosition] = useState<{ left: number; top: number }>(() =>
    computePosition(anchor, POPOVER_WIDTH, 320),
  );

  // Re-clamp position once we know the actual rendered height (long bodies
  // would otherwise push the popover off the bottom of the viewport).
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    const h = el.offsetHeight;
    setPosition(computePosition(anchor, POPOVER_WIDTH, h));
  }, [anchor, card.card_id, mode]);

  // Esc closes (or cancels edit if dirty — App handles the confirm dialog).
  // Capture phase + stopImmediatePropagation so we both beat AND suppress the
  // App-level Esc handler — otherwise App would also clear the selection on
  // the same key press.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        e.stopImmediatePropagation();
        onClose();
      }
    };
    window.addEventListener('keydown', onKey, true);
    return () => window.removeEventListener('keydown', onKey, true);
  }, [onClose]);

  // Click anywhere outside the popover closes it. Use mousedown so the close
  // fires before any text-selection click would otherwise trigger.
  useEffect(() => {
    const onDown = (e: MouseEvent) => {
      const el = ref.current;
      if (!el) return;
      if (e.target instanceof Node && el.contains(e.target)) return;
      onClose();
    };
    window.addEventListener('mousedown', onDown);
    return () => window.removeEventListener('mousedown', onDown);
  }, [onClose]);

  return (
    <div
      ref={ref}
      role="dialog"
      aria-modal="false"
      onClick={(e) => e.stopPropagation()}
      style={{
        position: 'fixed',
        left: position.left,
        top: position.top,
        width: POPOVER_WIDTH,
        maxHeight: `calc(100vh - ${VIEWPORT_PAD * 2}px)`,
        background: 'white',
        border: '1px solid var(--rule)',
        borderLeft: `3px solid ${spec.rule}`,
        borderRadius: 6,
        boxShadow: '0 16px 48px rgba(0,0,0,0.18), 0 2px 6px rgba(0,0,0,0.06)',
        display: 'flex',
        flexDirection: 'column',
        zIndex: 1500,
        overflow: 'hidden',
      }}
    >
      {mode === 'view' ? (
        <ViewBody
          card={card}
          knownIds={knownIds}
          projectId={projectId}
          onClose={onClose}
          onEnterEdit={() => setMode('edit')}
          onJumpToCard={onJumpToCard}
          onShowHistory={projectId ? () => setShowHistory(true) : undefined}
          onShowHistoryFor={onShowHistory}
          onSetImplementationStatus={onSetImplementationStatus}
          onArchive={onArchive}
        />
      ) : (
        <EditBody
          card={card}
          dirty={dirty}
          draftKind={draftKind}
          setDraftKind={setDraftKind}
          otherKind={otherKind}
          setOtherKind={setOtherKind}
          draftTitle={draftTitle}
          setDraftTitle={setDraftTitle}
          draftBody={draftBody}
          setDraftBody={setDraftBody}
          draftPack={draftPack}
          setDraftPack={setDraftPack}
          onSave={onSave}
          onDelete={onDelete}
          onSaveAndApprove={onSaveAndApprove}
          onCancel={() => {
            setDraftKind(card.kind);
            setDraftTitle(card.title ?? '');
            setDraftBody(card.body);
            setDraftPack(card.pack ?? '');
            setMode('view');
          }}
          onClose={onClose}
          onShowHistory={projectId ? () => setShowHistory(true) : undefined}
        />
      )}
      {showHistory && projectId && (
        <HistoryDrawer
          projectId={projectId}
          cardId={card.card_id}
          onClose={() => setShowHistory(false)}
          onRestored={onRestored}
        />
      )}
    </div>
  );
}

// ── view mode ─────────────────────────────────────────────────────

function ViewBody({
  card,
  knownIds,
  projectId,
  onClose,
  onEnterEdit,
  onJumpToCard,
  onShowHistory,
  onShowHistoryFor,
  onSetImplementationStatus,
  onArchive,
}: {
  card: Card;
  knownIds: ReadonlySet<string>;
  /** Used to compose the MCP `draftlize://<project>/<card>` URI shown
   *  in the footer. Optional so the peek still works in contexts that
   *  don't carry project state. */
  projectId?: string;
  onClose: () => void;
  onEnterEdit: () => void;
  onJumpToCard?: (id: string) => void;
  onShowHistory?: () => void;
  onShowHistoryFor?: (cardId: string) => void;
  onSetImplementationStatus?: (
    status: ImplementationStatus | null,
  ) => Promise<void>;
  onArchive?: (archived: boolean) => Promise<void>;
}) {
  const { t } = useTranslation();
  const spec = specForKind(card.kind);
  const [archiving, setArchiving] = useState(false);
  const edges = parseEdges(card.body);
  const computed = getCardComputed(card);
  const brokenRefs = parseAllRefs(card.body).filter(
    (id) => id !== card.card_id && !knownIds.has(id),
  );
  const brokenNotInEdges = brokenRefs.filter(
    (id) => !edges.some((e) => e.targetId === id),
  );

  return (
    <>
      {/* Header — kind + id + author + close */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          padding: '10px 12px 8px 14px',
          borderBottom: '1px solid var(--rule)',
          background: 'var(--paper-0)',
          flexShrink: 0,
        }}
      >
        <span
          className="mono"
          style={{
            fontSize: 10,
            color: spec.color,
            letterSpacing: '.12em',
            fontWeight: 700,
          }}
        >
          {spec.glyph} {spec.label}
        </span>
        <span className="mono" style={{ fontSize: 10, color: 'var(--ink-3)' }}>
          {card.card_id}
        </span>
        {card.stale && (
          <span
            className="mono"
            style={{
              fontSize: 9.5,
              color: 'var(--red)',
              fontWeight: 700,
              letterSpacing: '.05em',
            }}
          >
            {t('cardPeek.stale')}
          </span>
        )}
        <ImplementationStatusPill status={card.implementation_status} />
        {computed && <ComputedBadge />}
        <span style={{ flex: 1 }} />
        <span
          className="mono"
          style={{
            fontSize: 9.5,
            color: card.author === 'user' ? 'var(--blue)' : 'var(--ink-3)',
            letterSpacing: '.1em',
            fontWeight: 600,
          }}
        >
          {card.author === 'user' ? t('cardPeek.byYou') : t('cardPeek.byAgent')}
        </span>
        <button
          onClick={onClose}
          aria-label={t('cardPeek.closePeekAria')}
          className="mono"
          style={{
            fontSize: 14,
            lineHeight: 1,
            padding: '2px 6px',
            border: 'none',
            background: 'transparent',
            color: 'var(--ink-3)',
            cursor: 'pointer',
          }}
        >
          ×
        </button>
      </div>

      {/* Body — scrolls when long */}
      <div
        style={{
          padding: '14px 18px 16px',
          overflowY: 'auto',
          flex: 1,
        }}
      >
        {card.title && (
          <div
            style={{
              fontSize: 16,
              fontWeight: 600,
              color: 'var(--ink-0)',
              lineHeight: 1.35,
              marginBottom: card.body ? 10 : 0,
              textDecoration: card.stale ? 'line-through' : 'none',
            }}
          >
            {card.title}
          </div>
        )}
        {card.body && (
          <div
            className="md-card-body"
            style={{
              fontSize: 13,
              lineHeight: 1.65,
              color: 'var(--ink-1)',
            }}
          >
            <CardBodyMarkdown
              body={bodyWithoutEdgeLines(card.body)}
              knownIds={knownIds}
              onJumpToCard={onJumpToCard}
            />
          </div>
        )}
        {card.kind === 'decision' && (
          <DecisionExtras
            card={card}
            knownIds={knownIds}
            onJumpToCard={onJumpToCard}
          />
        )}
        {card.implementation_status && (
          <ImplementationTimeline status={card.implementation_status} />
        )}
        {edges.length > 0 && (
          <div
            style={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: 10,
              marginTop: 14,
              paddingTop: 10,
              borderTop: '1px dashed var(--rule)',
            }}
          >
            {edges.map((e, i) => {
              const known = knownIds.has(e.targetId) || e.targetId === card.card_id;
              const Tag: 'button' | 'span' = known && onJumpToCard ? 'button' : 'span';
              return (
                <Tag
                  key={i}
                  className="mono"
                  onClick={
                    known && onJumpToCard
                      ? (ev) => {
                          ev.stopPropagation();
                          onJumpToCard(e.targetId);
                        }
                      : undefined
                  }
                  title={known && onJumpToCard ? t('cardPeek.jumpTo', { id: e.targetId }) : undefined}
                  style={{
                    fontSize: 10.5,
                    color: edgeColor(e.type),
                    letterSpacing: '.04em',
                    background: 'none',
                    border: 'none',
                    padding: 0,
                    margin: 0,
                    font: 'inherit',
                    cursor: known && onJumpToCard ? 'pointer' : 'default',
                  }}
                >
                  <span style={{ marginRight: 4 }}>{edgeArrow(e.type)}</span>
                  {e.type} {e.targetId}
                  {!known && (
                    <span style={{ color: 'var(--red)', marginLeft: 4 }}>?</span>
                  )}
                </Tag>
              );
            })}
          </div>
        )}
        {brokenNotInEdges.length > 0 && (
          <div
            className="mono"
            style={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: 8,
              marginTop: edges.length > 0 ? 8 : 14,
              paddingTop: edges.length > 0 ? 0 : 10,
              borderTop: edges.length > 0 ? 'none' : '1px dashed var(--rule)',
              fontSize: 10.5,
              color: 'var(--red)',
              letterSpacing: '.04em',
            }}
            title={t('cardPeek.brokenRefsTooltip')}
          >
            <span style={{ fontWeight: 700 }}>{t('cardPeek.brokenRefs')}</span>
            {brokenNotInEdges.map((id) =>
              onShowHistoryFor ? (
                <button
                  key={id}
                  onClick={(e) => {
                    e.stopPropagation();
                    onShowHistoryFor(id);
                  }}
                  title={t('cardPeek.seeHistoryOfDeleted', { id })}
                  className="mono"
                  style={{
                    background: 'none',
                    border: 'none',
                    padding: 0,
                    margin: 0,
                    font: 'inherit',
                    color: 'var(--red)',
                    cursor: 'pointer',
                    textDecoration: 'underline',
                    textUnderlineOffset: 2,
                    textDecorationStyle: 'dotted',
                  }}
                >
                  [[{id}]]
                </button>
              ) : (
                <span key={id}>[[{id}]]</span>
              ),
            )}
          </div>
        )}
        {computed && <ComputedDetail computed={computed} />}
      </div>

      {/* MCP address strip — gives the user a one-click way to copy the
          `draftlize://<project>/<card>` URI so they can paste `@<URI>`
          into Claude Code / Cursor and pin this exact card as context.
          Only renders when projectId is known (peek opens from project-
          scoped contexts; should always be present in practice). */}
      {projectId && <UriStrip projectId={projectId} cardId={card.card_id} />}

      {/* Footer — edit + close hint */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          padding: '8px 12px 10px',
          borderTop: '1px solid var(--rule)',
          background: 'var(--paper-0)',
          flexShrink: 0,
        }}
      >
        <button
          onClick={onEnterEdit}
          className="mono"
          style={{
            fontSize: 11,
            padding: '5px 10px',
            border: '1px solid var(--rule)',
            borderRadius: 4,
            background: 'white',
            color: 'var(--ink-1)',
            fontWeight: 600,
            letterSpacing: '.06em',
            cursor: 'pointer',
          }}
        >
          {t('common.edit')}
        </button>
        {onShowHistory && (
          <button
            onClick={onShowHistory}
            className="mono"
            title={t('cardPeek.historyTooltip')}
            style={{
              fontSize: 11,
              padding: '5px 10px',
              border: '1px solid var(--rule)',
              borderRadius: 4,
              background: 'white',
              color: 'var(--ink-2)',
              fontWeight: 600,
              letterSpacing: '.06em',
              cursor: 'pointer',
            }}
          >
            {t('cardPeek.history')}
          </button>
        )}
        {onArchive && (
          <button
            onClick={async () => {
              if (archiving) return;
              setArchiving(true);
              try {
                // Server flips the bit + broadcasts CardUpdated, which the
                // App-level SSE handler picks up and refetches the cards
                // list. The peek closes once the toggle resolves so the
                // (now-hidden) card doesn't sit on screen looking stuck.
                await onArchive(!card.archived);
                onClose();
              } catch {
                // Swallow — surface via the global error toast layer.
              } finally {
                setArchiving(false);
              }
            }}
            disabled={archiving}
            className="mono"
            title={
              card.archived
                ? t('cardPeek.unarchiveTooltip')
                : t('cardPeek.archiveTooltip')
            }
            style={{
              fontSize: 11,
              padding: '5px 10px',
              border: '1px solid var(--rule)',
              borderRadius: 4,
              background: 'white',
              color: 'var(--ink-2)',
              fontWeight: 600,
              letterSpacing: '.06em',
              cursor: archiving ? 'wait' : 'pointer',
              opacity: archiving ? 0.6 : 1,
            }}
          >
            {card.archived ? t('cardPeek.unarchive') : t('cardPeek.archive')}
          </button>
        )}
        {/* PR-41: implementation-status dropdown. Only renders for cards
            already opted into tracking (status != null) so we don't show
            the control on every card peek — flipping NULL → tracked is
            currently a CLI/MCP move (see PR-42 for an inline upgrade).
            Choices include the four allowlist values and "(clear)". */}
        {onSetImplementationStatus &&
          (card.implementation_status === 'built' ||
            card.implementation_status === 'drift_detected') && (
            <ImplementationStatusDropdown
              current={card.implementation_status}
              onChange={onSetImplementationStatus}
            />
          )}
        <span style={{ flex: 1 }} />
        <span
          className="mono"
          style={{ fontSize: 9.5, color: 'var(--ink-3)', letterSpacing: '.06em' }}
        >
          {t('cardPeek.closeHint')}
        </span>
      </div>
    </>
  );
}

/** Footer dropdown for the four implementation states + clear. Renders
 *  inline as a `<select>` styled to match the adjacent text buttons —
 *  no fancy custom-popover, the surface is "tweak the badge" not a
 *  primary action. While the change is in flight the control is
 *  disabled so the user doesn't double-fire. */
function ImplementationStatusDropdown({
  current,
  onChange,
}: {
  current: ImplementationStatus;
  onChange: (status: ImplementationStatus | null) => Promise<void>;
}) {
  const { t } = useTranslation();
  const [busy, setBusy] = useState(false);
  return (
    <select
      className="mono"
      disabled={busy}
      value={current}
      title={t('cardPeek.markStatusTooltip')}
      onChange={async (e) => {
        const raw = e.target.value;
        const next: ImplementationStatus | null =
          raw === '' ? null : (raw as ImplementationStatus);
        if (next === current) return;
        setBusy(true);
        try {
          await onChange(next);
        } finally {
          setBusy(false);
        }
      }}
      style={{
        fontSize: 11,
        padding: '4px 8px',
        border: '1px solid var(--rule)',
        borderRadius: 4,
        background: 'white',
        color: 'var(--ink-2)',
        fontWeight: 600,
        letterSpacing: '.04em',
        cursor: busy ? 'wait' : 'pointer',
      }}
    >
      <option value="proposed">{t('cardPeek.statusProposed')}</option>
      <option value="building">{t('cardPeek.statusBuilding')}</option>
      <option value="built">{t('cardPeek.statusBuilt')}</option>
      <option value="drift_detected">{t('cardPeek.statusDriftDetected')}</option>
      <option value="">{t('cardPeek.statusClear')}</option>
    </select>
  );
}

/** doc/12 §3.5.3: four-state implementation timeline. Renders only for
 *  tracked cards (status != null) inside the peek body. The first three
 *  states are the happy path (proposed → building → built);
 *  drift_detected is the red branch AFTER built — the card changed under
 *  a shipped implementation — so its connector is dashed: it should read
 *  as a detour, not the natural next step. */
const IMPL_TIMELINE_STAGES: ReadonlyArray<{
  key: ImplementationStatus;
  icon: string;
  color: string;
}> = [
  { key: 'proposed', icon: '○', color: 'var(--ink-2)' },
  { key: 'building', icon: '◐', color: 'var(--amber)' },
  { key: 'built', icon: '●', color: 'var(--green)' },
  { key: 'drift_detected', icon: '⚠', color: 'var(--red)' },
];

function ImplementationTimeline({ status }: { status: ImplementationStatus }) {
  const { t } = useTranslation();
  // Index the card has reached. drift_detected implies the card WAS built
  // first, so every earlier stage renders as "passed".
  const reached = IMPL_TIMELINE_STAGES.findIndex((s) => s.key === status);
  return (
    <div
      style={{
        marginTop: 14,
        paddingTop: 10,
        borderTop: '1px dashed var(--rule)',
      }}
    >
      <div
        className="mono"
        style={{
          fontSize: 9,
          color: 'var(--ink-3)',
          letterSpacing: '.1em',
          fontWeight: 700,
          marginBottom: 8,
        }}
      >
        {t('cardPeek.implTimelineLabel')}
      </div>
      <div style={{ display: 'flex', alignItems: 'flex-start' }}>
        {IMPL_TIMELINE_STAGES.map((s, i) => {
          const isCurrent = i === reached;
          const passed = i < reached;
          const isDriftStage = s.key === 'drift_detected';
          return (
            <div
              key={s.key}
              style={{ display: 'flex', alignItems: 'flex-start', flex: i === 0 ? '0 0 auto' : 1 }}
            >
              {i > 0 && (
                <div
                  style={{
                    flex: 1,
                    marginTop: 8,
                    // Dashed into drift — it's a branch, not the next stop.
                    borderTop: `1px ${isDriftStage ? 'dashed' : 'solid'} ${
                      i <= reached
                        ? (isDriftStage ? 'var(--red)' : 'var(--ink-3)')
                        : 'var(--rule)'
                    }`,
                    minWidth: 14,
                  }}
                />
              )}
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: 2,
                  padding: '0 4px',
                }}
              >
                <span
                  style={{
                    fontSize: 13,
                    lineHeight: '17px',
                    color: isCurrent || passed ? s.color : 'var(--ink-4)',
                    opacity: passed ? 0.55 : 1,
                    fontWeight: isCurrent ? 700 : 400,
                  }}
                >
                  {s.icon}
                </span>
                <span
                  className="mono"
                  style={{
                    fontSize: 8.5,
                    letterSpacing: '.06em',
                    color: isCurrent ? s.color : passed ? 'var(--ink-3)' : 'var(--ink-4)',
                    fontWeight: isCurrent ? 700 : 500,
                    whiteSpace: 'nowrap',
                  }}
                >
                  {t(`cardPeek.implTimeline.${s.key}.label`)}
                </span>
              </div>
            </div>
          );
        })}
      </div>
      {/* One-line narrative for the current state — this is where the
          §3.5.3 "drift tells a story" copy lives. */}
      <div
        style={{
          marginTop: 6,
          fontSize: 10.5,
          lineHeight: 1.5,
          color: status === 'drift_detected' ? 'var(--red)' : 'var(--ink-3)',
        }}
      >
        {t(`cardPeek.implTimeline.${status}.note`)}
      </div>
    </div>
  );
}

// ── edit mode ─────────────────────────────────────────────────────

function EditBody({
  card,
  dirty,
  draftKind,
  setDraftKind,
  otherKind,
  setOtherKind,
  draftTitle,
  setDraftTitle,
  draftBody,
  setDraftBody,
  draftPack,
  setDraftPack,
  onSave,
  onDelete,
  onSaveAndApprove,
  onCancel,
  onClose,
  onShowHistory,
}: {
  card: Card;
  dirty: boolean;
  draftKind: string;
  setDraftKind: (s: string) => void;
  otherKind: string;
  setOtherKind: (s: string) => void;
  draftTitle: string;
  setDraftTitle: (s: string) => void;
  draftBody: string;
  setDraftBody: (s: string) => void;
  draftPack: string;
  setDraftPack: (s: string) => void;
  onSave: (patch: { kind: string; title: string; body: string; pack: string }) => Promise<void>;
  onDelete: (reason?: string) => Promise<void>;
  onSaveAndApprove?: (patch: { title: string; body: string }) => Promise<void>;
  onCancel: () => void;
  onClose: () => void;
  onShowHistory?: () => void;
}) {
  const { t } = useTranslation();
  const [saving, setSaving] = useState<null | 'save' | 'approve'>(null);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [deleteReason, setDeleteReason] = useState('');

  // Every gap card is a proposal under the unified gap model — `gap_kind`
  // is always `interpretation_needed` for newly-written gaps, and legacy
  // gaps from earlier taxonomies (fact_needed / decision / conflict) still
  // promote through the same path.
  const isProposal = card.kind === 'gap';
  const showApprove = isProposal && !!onSaveAndApprove;
  const proposedKind =
    typeof card.extra?.['proposed_kind'] === 'string'
      ? (card.extra['proposed_kind'] as string)
      : 'card';

  // Reset confirm-delete prompt when card changes.
  useEffect(() => {
    setConfirmDelete(false);
    setDeleteReason('');
  }, [card.card_id]);

  const isOtherKind = !KIND_SPECS.some((s) => s.id === draftKind);
  const effectiveKind = isOtherKind ? (otherKind || draftKind) : draftKind;
  const spec = specForKind(card.kind);

  const submit = async () => {
    setSaving('save');
    try {
      await onSave({
        kind: effectiveKind.trim() || card.kind,
        title: draftTitle,
        body: draftBody,
        pack: draftPack,
      });
    } finally {
      setSaving(null);
    }
  };

  /** Edit-and-promote in one round-trip. Skips the regular onSave PATCH
   *  because /promote already accepts title/body for "Edit & Approve". */
  const submitAndApprove = async () => {
    if (!onSaveAndApprove) return;
    setSaving('approve');
    try {
      await onSaveAndApprove({ title: draftTitle, body: draftBody });
    } finally {
      setSaving(null);
    }
  };

  // Body textarea autosize — grow with content up to half the viewport so
  // a long-body edit doesn't get cramped, but never push the popover off
  // the bottom of the screen.
  const bodyRef = useRef<HTMLTextAreaElement>(null);
  useLayoutEffect(() => {
    const el = bodyRef.current;
    if (!el) return;
    el.style.height = 'auto';
    const max = Math.floor(window.innerHeight * 0.5);
    el.style.height = `${Math.min(el.scrollHeight, max)}px`;
  }, [draftBody]);

  return (
    <>
      <div
        style={{
          padding: '10px 12px 8px 14px',
          borderBottom: '1px solid var(--rule)',
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          background: 'var(--paper-0)',
          flexShrink: 0,
        }}
      >
        <span
          className="mono"
          style={{ fontSize: 10, color: spec.color, letterSpacing: '.12em', fontWeight: 700 }}
        >
          {t('cardPeek.editLabel')} · {card.card_id}
        </span>
        {dirty && (
          <span
            className="mono"
            style={{ fontSize: 9.5, color: 'var(--amber)', fontWeight: 700, letterSpacing: '.05em' }}
          >
            {t('cardPeek.unsaved')}
          </span>
        )}
        <span style={{ flex: 1 }} />
        {onShowHistory && (
          <button
            onClick={onShowHistory}
            className="mono"
            title={t('cardPeek.historyTooltip')}
            style={{
              fontSize: 10,
              padding: '3px 8px',
              border: '1px solid var(--rule)',
              borderRadius: 4,
              background: 'white',
              color: 'var(--ink-2)',
              fontWeight: 600,
              letterSpacing: '.06em',
              cursor: 'pointer',
            }}
          >
            {t('cardPeek.history')}
          </button>
        )}
        <button
          onClick={onClose}
          aria-label={t('common.close')}
          className="mono"
          style={{
            fontSize: 14,
            lineHeight: 1,
            padding: '2px 6px',
            border: 'none',
            background: 'transparent',
            color: 'var(--ink-3)',
            cursor: 'pointer',
          }}
        >
          ×
        </button>
      </div>

      <div style={{ flex: 1, padding: '14px 18px', overflowY: 'auto' }}>
        <Field label={t('cardPeek.fieldKind')}>
          <select
            value={isOtherKind ? '__other' : draftKind}
            onChange={(e) => {
              if (e.target.value === '__other') {
                setDraftKind('');
                setOtherKind('');
              } else {
                setDraftKind(e.target.value);
              }
            }}
            style={selectStyle}
          >
            {KIND_SPECS.map((k) => (
              <option key={k.id} value={k.id}>{k.id}</option>
            ))}
            <option value="__other">{t('cardPeek.kindOther')}</option>
          </select>
          {isOtherKind && (
            <input
              value={otherKind}
              onChange={(e) => {
                setOtherKind(e.target.value);
                setDraftKind(e.target.value);
              }}
              placeholder={t('cardPeek.customKindPlaceholder')}
              style={{ ...inputStyle, marginTop: 6 }}
            />
          )}
        </Field>
        <Field label={t('cardPeek.fieldTitle')}>
          <input
            value={draftTitle}
            onChange={(e) => setDraftTitle(e.target.value)}
            style={inputStyle}
            autoFocus
          />
        </Field>
        <Field label={t('cardPeek.fieldBody')}>
          <textarea
            ref={bodyRef}
            value={draftBody}
            onChange={(e) => setDraftBody(e.target.value)}
            rows={8}
            style={{
              ...inputStyle,
              fontFamily: 'JetBrains Mono, monospace',
              fontSize: 12.5,
              lineHeight: 1.6,
              resize: 'vertical',
            }}
          />
        </Field>
        <Field label={t('cardPeek.fieldPack')}>
          <input
            value={draftPack}
            onChange={(e) => setDraftPack(e.target.value)}
            placeholder={t('cardPeek.packPlaceholder')}
            style={inputStyle}
          />
        </Field>
        {/* doc/11 PR-A2: read-only display of decision extras during edit.
            v1 doesn't expose an editor for these — agent writes them via
            write_card's extra param. Visible during edit so the user sees
            what's already there. */}
        {effectiveKind === 'decision' && (
          <DecisionExtras card={card} knownIds={new Set()} />
        )}
      </div>

      <div
        style={{
          padding: '8px 12px 10px',
          borderTop: '1px solid var(--rule)',
          background: 'var(--paper-1)',
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          flexShrink: 0,
        }}
      >
        {!confirmDelete && (
          <>
            <button
              onClick={onCancel}
              disabled={saving !== null}
              className="mono"
              style={btn('white', 'var(--ink-1)', true)}
            >
              {t('common.cancel')}
            </button>
            <button
              onClick={submit}
              disabled={!dirty || saving !== null}
              className="mono"
              style={{
                // For proposals, the primary CTA is "Save & Approve" — give
                // plain Save a quieter treatment to push the user toward the
                // intended next step.
                ...btn(
                  dirty ? (showApprove ? 'white' : 'var(--ink-0)') : 'var(--ink-4)',
                  showApprove ? 'var(--ink-1)' : 'white',
                  showApprove,
                ),
                cursor: dirty && saving === null ? 'pointer' : 'not-allowed',
              }}
              title={
                showApprove
                  ? t('cardPeek.saveGapTooltip')
                  : t('cardPeek.saveChangesTooltip')
              }
            >
              {saving === 'save' ? t('cardPeek.saving') : t('common.save')}
            </button>
            {showApprove && (
              <button
                onClick={submitAndApprove}
                disabled={saving !== null}
                className="mono"
                style={{
                  ...btn('var(--ink-0)', 'white'),
                  cursor: saving === null ? 'pointer' : 'not-allowed',
                }}
                title={t('cardPeek.approveTooltip', { kind: proposedKind })}
              >
                {saving === 'approve'
                  ? t('cardPeek.approving')
                  : t('cardPeek.saveAndApprove', { kind: proposedKind })}
              </button>
            )}
            <span style={{ flex: 1 }} />
            <button
              onClick={() => setConfirmDelete(true)}
              disabled={saving !== null}
              className="mono"
              style={btn('white', 'var(--red)', true)}
            >
              {isProposal ? t('cardPeek.reject') : t('common.delete')}
            </button>
          </>
        )}
        {confirmDelete && (
          <>
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                gap: 6,
                flex: 1,
                minWidth: 0,
              }}
            >
              <span style={{ fontSize: 12, color: 'var(--red)' }}>
                {isProposal
                  ? t('cardPeek.confirmReject', { id: card.card_id })
                  : t('cardPeek.confirmDelete', { id: card.card_id })}
              </span>
              {/* Reason input — optional; persists to card_history.delete_reason
                  so the Deleted view shows "you deleted: <reason>" later.
                  Hidden for proposals (they go through reject_gap which has
                  its own reason flow in GapLedger). */}
              {!isProposal && (
                <input
                  value={deleteReason}
                  onChange={(e) => setDeleteReason(e.target.value)}
                  placeholder={t('cardPeek.deleteReasonPlaceholder')}
                  style={{
                    fontSize: 12,
                    padding: '5px 8px',
                    border: '1px solid var(--rule)',
                    borderRadius: 4,
                    outline: 'none',
                    background: 'white',
                  }}
                />
              )}
            </div>
            <button
              onClick={() => setConfirmDelete(false)}
              disabled={saving !== null}
              className="mono"
              style={btn('white', 'var(--ink-1)', true)}
            >
              {t('common.cancel')}
            </button>
            <button
              onClick={async () => {
                setSaving('save');
                try {
                  await onDelete(deleteReason.trim() || undefined);
                } finally {
                  setSaving(null);
                }
              }}
              disabled={saving !== null}
              className="mono"
              style={btn('var(--red)', 'white')}
            >
              {saving === 'save'
                ? t('cardPeek.deleting')
                : isProposal
                  ? t('cardPeek.yesReject')
                  : t('cardPeek.yesDelete')}
            </button>
          </>
        )}
      </div>
    </>
  );
}

/**
 * doc/11 PR-A2 — render the optional decision-extras (evidence /
 * alternatives / invalidated_if) as styled chips + text. Read-only for v1
 * (the agent writes these via write_card's `extra` param; CardPeek-side
 * editing is a follow-up). Renders nothing if none of the three fields are
 * present, so non-augmented decision cards see no UI churn.
 *
 * The evidence-stale signal lives on the *decision* (cards.stale=TRUE when
 * one of the evidence cards is patched), so this section doesn't compute
 * its own freshness — the existing `card.stale` chip already covers it.
 */
function DecisionExtras({
  card,
  knownIds,
  onJumpToCard,
}: {
  card: Card;
  knownIds: ReadonlySet<string>;
  onJumpToCard?: (id: string) => void;
}) {
  const evidence = Array.isArray(card.extra?.evidence)
    ? (card.extra.evidence as unknown[]).filter((v): v is string => typeof v === 'string')
    : [];
  const alternatives = Array.isArray(card.extra?.alternatives)
    ? (card.extra.alternatives as unknown[]).filter((v): v is string => typeof v === 'string')
    : [];
  const invalidatedIf =
    typeof card.extra?.invalidated_if === 'string' ? (card.extra.invalidated_if as string) : '';

  if (evidence.length === 0 && alternatives.length === 0 && !invalidatedIf) {
    return null;
  }

  const chipStyle: React.CSSProperties = {
    fontSize: 11,
    padding: '3px 8px',
    border: '1px solid var(--rule)',
    borderRadius: 4,
    background: 'white',
    color: 'var(--ink-1)',
    fontWeight: 600,
    letterSpacing: '.04em',
    cursor: 'pointer',
    textDecoration: 'none',
  };
  const unknownChipStyle: React.CSSProperties = {
    ...chipStyle,
    color: 'var(--ink-3)',
    cursor: 'default',
    textDecoration: 'line-through',
  };

  const renderChips = (ids: string[]) =>
    ids.map((id) => {
      const known = knownIds.has(id);
      const Tag: 'button' | 'span' = known && onJumpToCard ? 'button' : 'span';
      return (
        <Tag
          key={id}
          className="mono"
          onClick={known && onJumpToCard ? () => onJumpToCard(id) : undefined}
          style={known ? chipStyle : unknownChipStyle}
          title={known ? `Jump to ${id}` : `${id} not in this project (deleted or moved)`}
        >
          {id}
        </Tag>
      );
    });

  return (
    <div
      style={{
        marginTop: 14,
        paddingTop: 10,
        borderTop: '1px dashed var(--rule)',
        display: 'flex',
        flexDirection: 'column',
        gap: 10,
      }}
    >
      {evidence.length > 0 && (
        <div>
          <div
            className="mono"
            style={{
              fontSize: 9.5,
              color: 'var(--ink-3)',
              letterSpacing: '.1em',
              fontWeight: 700,
              marginBottom: 5,
            }}
          >
            EVIDENCE · {evidence.length}
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>{renderChips(evidence)}</div>
        </div>
      )}
      {alternatives.length > 0 && (
        <div>
          <div
            className="mono"
            style={{
              fontSize: 9.5,
              color: 'var(--ink-3)',
              letterSpacing: '.1em',
              fontWeight: 700,
              marginBottom: 5,
            }}
          >
            ALTERNATIVES · {alternatives.length}
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
            {renderChips(alternatives)}
          </div>
        </div>
      )}
      {invalidatedIf && (
        <div>
          <div
            className="mono"
            style={{
              fontSize: 9.5,
              color: 'var(--ink-3)',
              letterSpacing: '.1em',
              fontWeight: 700,
              marginBottom: 4,
            }}
          >
            INVALIDATED IF
          </div>
          <div
            style={{
              fontSize: 12.5,
              lineHeight: 1.55,
              color: 'var(--ink-2)',
              fontStyle: 'italic',
              padding: '6px 10px',
              borderLeft: '2px solid var(--amber)',
              background: 'var(--amber-soft)',
            }}
          >
            {invalidatedIf}
          </div>
        </div>
      )}
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div style={{ marginBottom: 14 }}>
      <div
        className="mono"
        style={{
          fontSize: 9.5,
          color: 'var(--ink-3)',
          letterSpacing: '.1em',
          fontWeight: 700,
          marginBottom: 4,
        }}
      >
        {label}
      </div>
      {children}
    </div>
  );
}

const inputStyle: React.CSSProperties = {
  width: '100%',
  fontSize: 13,
  padding: '7px 10px',
  border: '1px solid var(--rule)',
  borderRadius: 5,
  outline: 'none',
  background: 'white',
};

const selectStyle: React.CSSProperties = {
  ...inputStyle,
  fontFamily: 'JetBrains Mono, monospace',
  fontSize: 12.5,
};

function btn(bg: string, color: string, bordered = false): React.CSSProperties {
  return {
    fontSize: 11.5,
    letterSpacing: '.04em',
    padding: '6px 12px',
    background: bg,
    color,
    border: bordered ? '1px solid var(--rule)' : '1px solid transparent',
    borderRadius: 4,
    fontWeight: 600,
    cursor: 'pointer',
  };
}

/**
 * Pick the popover position based on where the originating card is on screen.
 * Preferred placement: floating to the LEFT of the card, with the right edge
 * 12px before the card's left edge. If there isn't room left of the card
 * (narrow viewport, card near the left), we fall back to centering the
 * popover horizontally so it stays fully on-screen.
 *
 * Vertically: align the popover's top with the card's top, but clamp to the
 * viewport so a card near the bottom doesn't push the popover off-screen.
 */
function computePosition(
  anchor: DOMRect | null,
  width: number,
  estimatedHeight: number,
): { left: number; top: number } {
  const vw = typeof window !== 'undefined' ? window.innerWidth : 1280;
  const vh = typeof window !== 'undefined' ? window.innerHeight : 800;

  if (!anchor) {
    return {
      left: Math.max(VIEWPORT_PAD, (vw - width) / 2),
      top: Math.max(VIEWPORT_PAD, (vh - estimatedHeight) / 2),
    };
  }

  const gap = 12;
  const desiredLeft = anchor.left - gap - width;
  const left =
    desiredLeft >= VIEWPORT_PAD
      ? desiredLeft
      : Math.max(VIEWPORT_PAD, (vw - width) / 2);

  const maxTop = Math.max(VIEWPORT_PAD, vh - estimatedHeight - VIEWPORT_PAD);
  const top = Math.max(VIEWPORT_PAD, Math.min(anchor.top, maxTop));

  return { left, top };
}

/**
 * Thin row above CardPeek's footer that exposes the card's MCP address.
 * Two purposes:
 *   1. Lets the user copy `draftlize://<project>/<card>` so they can
 *      drop `@<URI>` into Claude Code / Cursor and pin the exact card
 *      as context — much more precise than asking the agent to search.
 *   2. Acts as visual proof that this artifact has a stable, external
 *      address (the "addressable cards" bet from doc/01 §4.1).
 *
 * Click toggles a 1.5s "copied ✓" pill so the action has a visible
 * receipt without blocking on toast infra.
 */
function UriStrip({ projectId, cardId }: { projectId: string; cardId: string }) {
  const { t } = useTranslation();
  const uri = `draftlize://${projectId}/${cardId}`;
  const [copied, setCopied] = useState(false);
  const onCopy = async () => {
    try {
      await navigator.clipboard.writeText(uri);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      // Insecure context / perm denied — silently no-op; user can
      // select the text and copy manually.
    }
  };
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        padding: '5px 12px 5px 14px',
        borderTop: '1px solid var(--rule)',
        background: 'var(--paper-1)',
        flexShrink: 0,
      }}
    >
      <span
        className="mono"
        style={{
          fontSize: 9.5,
          color: 'var(--ink-3)',
          letterSpacing: '.08em',
          fontWeight: 600,
        }}
      >
        MCP
      </span>
      <code
        className="mono"
        title={t('cardPeek.uriTooltip')}
        style={{
          fontSize: 10.5,
          color: 'var(--ink-2)',
          flex: 1,
          minWidth: 0,
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        }}
      >
        {uri}
      </code>
      <button
        onClick={onCopy}
        className="mono"
        style={{
          fontSize: 10,
          padding: '2px 8px',
          border: '1px solid var(--rule)',
          borderRadius: 3,
          background: copied ? 'var(--paper-0)' : 'white',
          color: 'var(--ink-2)',
          cursor: 'pointer',
        }}
      >
        {copied ? '✓' : t('cardPeek.copy')}
      </button>
    </div>
  );
}

/** Same edge-line stripping as CardRow — keeps rendered body free of the
 *  bare wikilink lines that we surface as chips below the body. */
function bodyWithoutEdgeLines(body: string): string {
  return body
    .split('\n')
    .filter((line) => {
      const trimmed = line.trim();
      if (trimmed === '') return true;
      const stripped = trimmed.replace(
        /(?:→|->)\s*[a-z][a-z0-9-]*\s*\[\[[A-Za-z0-9_-]+\]\]\s*/g,
        '',
      );
      return stripped.length > 0;
    })
    .join('\n')
    .trim();
}
